import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useCreateTask, useUpdateTask, useDeleteTask } from '@/hooks/useTasks';
import { logger } from '@/utils/logger';

// Helper to get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

// Calculate end date based on start date and duration (hours)
// Assumes 8 hours per working day, 5 days per week (Mon-Fri)
const calculateEndDate = (startDate: string, durationHours: number): string => {
  if (!startDate || durationHours <= 0) return '';

  const HOURS_PER_DAY = 8;
  const workingDaysNeeded = Math.ceil(durationHours / HOURS_PER_DAY);

  const start = new Date(startDate);
  let daysAdded = 0;
  let currentDate = new Date(start);

  while (daysAdded < workingDaysNeeded) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dayOfWeek = currentDate.getDay();
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }

  return currentDate.toISOString().split('T')[0];
};

// Calculate start date by going backward from end date by duration
const calculateStartDate = (endDate: string, durationHours: number): string => {
  if (!endDate || durationHours <= 0) return '';

  const HOURS_PER_DAY = 8;
  const workingDaysNeeded = Math.ceil(durationHours / HOURS_PER_DAY);

  const end = new Date(endDate);
  let daysSubtracted = 0;
  let currentDate = new Date(end);

  while (daysSubtracted < workingDaysNeeded) {
    currentDate.setDate(currentDate.getDate() - 1);
    const dayOfWeek = currentDate.getDay();
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysSubtracted++;
    }
  }

  return currentDate.toISOString().split('T')[0];
};

// Get next working day after a given date
const getNextWorkingDay = (dateStr: string): string => {
  if (!dateStr) return '';
  let currentDate = new Date(dateStr);
  currentDate.setDate(currentDate.getDate() + 1);

  // Skip weekends
  while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return currentDate.toISOString().split('T')[0];
};

// Get previous working day before a given date
const getPreviousWorkingDay = (dateStr: string): string => {
  if (!dateStr) return '';
  let currentDate = new Date(dateStr);
  currentDate.setDate(currentDate.getDate() - 1);

  // Skip weekends
  while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
    currentDate.setDate(currentDate.getDate() - 1);
  }

  return currentDate.toISOString().split('T')[0];
};
import {
  ITask,
  ITaskCreateRequest,
  TaskStatus,
  TaskPriority,
  TASK_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
} from '@/types/task';
import { toast } from 'react-hot-toast';
import EmployeeSelect from '@/components/ui/EmployeeSelect';

interface ProjectTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: ITask | null;
  allTasks: ITask[];
  projectId: string;
  projectName: string;
  phaseId: string;
  phaseName: string;
  initialPriorDep?: ITask | null;  // Pre-set prior dependency (for creating post-dependent task)
  initialPostDep?: ITask | null;   // Pre-set post dependency (for creating prior-dependent task)
}

const STATUS_OPTIONS: TaskStatus[] = ['plan', 'doing', 'review', 'done'];
const PRIORITY_OPTIONS: TaskPriority[] = ['low', 'medium', 'high', 'critical'];

const ProjectTaskModal: React.FC<ProjectTaskModalProps> = ({
  isOpen,
  onClose,
  task,
  allTasks,
  projectId,
  projectName,
  phaseId,
  phaseName,
  initialPriorDep,
  initialPostDep,
}) => {
  const { t } = useTranslation('project');
  const [formData, setFormData] = useState({
    task_name: '',
    task_owner: '',
    status: 'plan' as TaskStatus,
    priority: 'medium' as TaskPriority,
    duration: 1,
    planned_start: '',
    planned_end: '',
    prior_deps: [] as string[],
    post_deps: [] as string[],
  });

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const isEditing = !!task;
  const isReviewStatus = formData.status === 'review';

  // Populate form when editing or when creating with initial dependencies
  useEffect(() => {
    if (task) {
      setFormData({
        task_name: task.task_name,
        task_owner: task.task_owner,
        status: task.status,
        priority: task.priority,
        duration: task.duration,
        planned_start: task.planned_start || '',
        planned_end: task.planned_end || '',
        prior_deps: task.prior_deps || [],
        post_deps: task.post_deps || [],
      });
    } else {
      // New task - check for initial dependencies from + buttons
      const defaultDuration = 8; // 8 hours = 1 working day
      let startDate = getTodayDate();
      let endDate = calculateEndDate(startDate, defaultDuration);
      let priorDeps: string[] = [];
      let postDeps: string[] = [];

      // If creating with a prior dependency (clicked + on right side of existing task)
      // The new task should start after the prior task ends
      if (initialPriorDep) {
        priorDeps = [initialPriorDep.id];
        if (initialPriorDep.planned_end) {
          startDate = getNextWorkingDay(initialPriorDep.planned_end);
          endDate = calculateEndDate(startDate, defaultDuration);
        }
      }

      // If creating with a post dependency (clicked + on left side of existing task)
      // The new task should end before the post task starts
      if (initialPostDep) {
        postDeps = [initialPostDep.id];
        if (initialPostDep.planned_start) {
          endDate = getPreviousWorkingDay(initialPostDep.planned_start);
          startDate = calculateStartDate(endDate, defaultDuration);
        }
      }

      setFormData({
        task_name: '',
        task_owner: '',
        status: 'plan',
        priority: 'medium',
        duration: defaultDuration,
        planned_start: startDate,
        planned_end: endDate,
        prior_deps: priorDeps,
        post_deps: postDeps,
      });
    }
  }, [task, initialPriorDep, initialPostDep]);

  // Get the latest end date from prior dependencies (determines min start date)
  const getLatestPriorEndDate = useCallback((priorDeps: string[]): string | null => {
    if (priorDeps.length === 0) return null;

    let latestDate: string | null = null;
    for (const depId of priorDeps) {
      const depTask = allTasks.find(t => t.id === depId);
      if (depTask?.planned_end) {
        if (!latestDate || depTask.planned_end > latestDate) {
          latestDate = depTask.planned_end;
        }
      }
    }
    return latestDate;
  }, [allTasks]);

  // Get the earliest start date from post dependencies (determines max end date)
  const getEarliestPostStartDate = useCallback((postDeps: string[]): string | null => {
    if (postDeps.length === 0) return null;

    let earliestDate: string | null = null;
    for (const depId of postDeps) {
      const depTask = allTasks.find(t => t.id === depId);
      if (depTask?.planned_start) {
        if (!earliestDate || depTask.planned_start < earliestDate) {
          earliestDate = depTask.planned_start;
        }
      }
    }
    return earliestDate;
  }, [allTasks]);

  // Calculate date constraints based on current dependencies
  const dateConstraints = React.useMemo(() => {
    const latestPriorEnd = getLatestPriorEndDate(formData.prior_deps);
    const earliestPostStart = getEarliestPostStartDate(formData.post_deps);

    return {
      // Min start date: day after latest prior dependency ends
      minStartDate: latestPriorEnd ? getNextWorkingDay(latestPriorEnd) : undefined,
      // Max end date: day before earliest post dependency starts
      maxEndDate: earliestPostStart ? getPreviousWorkingDay(earliestPostStart) : undefined,
    };
  }, [formData.prior_deps, formData.post_deps, getLatestPriorEndDate, getEarliestPostStartDate]);

  // Auto-calculate end date when duration or start date changes
  const updateEndDate = useCallback((startDate: string, duration: number, maxEndDate?: string) => {
    let endDate = calculateEndDate(startDate, duration);
    // Ensure end date doesn't exceed max constraint from post dependencies
    if (maxEndDate && endDate > maxEndDate) {
      endDate = maxEndDate;
    }
    setFormData(prev => ({ ...prev, planned_end: endDate }));
  }, []);

  const handleDurationChange = (newDuration: number) => {
    setFormData(prev => ({ ...prev, duration: newDuration }));
    // Recalculate end date based on start and new duration
    updateEndDate(formData.planned_start, newDuration, dateConstraints.maxEndDate);
  };

  const handleStartDateChange = (newStartDate: string) => {
    setFormData(prev => ({ ...prev, planned_start: newStartDate }));
    updateEndDate(newStartDate, formData.duration, dateConstraints.maxEndDate);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.task_name.trim()) {
      toast.error(t('validation.taskNameRequired', 'Task name is required'));
      return;
    }

    if (!formData.task_owner.trim()) {
      toast.error(t('validation.taskOwnerRequired', 'Task owner is required'));
      return;
    }

    try {
      if (isEditing && task) {
        await updateTask.mutateAsync({
          id: task.id,
          data: {
            ...formData,
            project_id: projectId,
            project_name: projectName,
            phase_id: phaseId,
            phase_name: phaseName,
          },
        });
        toast.success(t('tasks.updateSuccess', 'Task updated'));
      } else {
        const createData: ITaskCreateRequest = {
          ...formData,
          project_id: projectId,
          project_name: projectName,
          phase_id: phaseId,
          phase_name: phaseName,
        };
        await createTask.mutateAsync(createData);
        toast.success(t('tasks.createSuccess', 'Task created'));
      }
      onClose();
    } catch (error) {
      toast.error(isEditing ? t('tasks.updateError', 'Failed to update task') : t('tasks.createError', 'Failed to create task'));
      logger.error('Task operation error:', error);
    }
  };

  const handleDelete = async () => {
    if (!task) return;

    if (!window.confirm(t('tasks.confirmDelete', 'Are you sure you want to delete this task?'))) {
      return;
    }

    try {
      await deleteTask.mutateAsync(task.id);
      toast.success(t('tasks.deleteSuccess', 'Task deleted'));
      onClose();
    } catch (error) {
      toast.error(t('tasks.deleteError', 'Failed to delete task'));
      logger.error('Delete task error:', error);
    }
  };

  const handleDependencyToggle = (
    type: 'prior_deps' | 'post_deps',
    taskId: string
  ) => {
    setFormData((prev) => {
      const deps = prev[type];
      const isRemoving = deps.includes(taskId);
      const newDeps = isRemoving
        ? deps.filter((id) => id !== taskId)
        : [...deps, taskId];

      let newFormData = { ...prev, [type]: newDeps };

      if (type === 'prior_deps' && !isRemoving) {
        // When adding a prior dependency, set start date to day after the latest prior's end date
        const latestPriorEnd = getLatestPriorEndDate(newDeps);
        if (latestPriorEnd) {
          const newStartDate = getNextWorkingDay(latestPriorEnd);
          const newEndDate = calculateEndDate(newStartDate, prev.duration);

          // Check if new end date conflicts with post dependencies
          const earliestPostStart = getEarliestPostStartDate(prev.post_deps);
          const maxEndDate = earliestPostStart ? getPreviousWorkingDay(earliestPostStart) : null;

          newFormData = {
            ...newFormData,
            planned_start: newStartDate,
            planned_end: maxEndDate && newEndDate > maxEndDate ? maxEndDate : newEndDate,
          };
        }
      } else if (type === 'post_deps' && !isRemoving) {
        // When adding a post dependency, set end date to day before the earliest post's start date
        const earliestPostStart = getEarliestPostStartDate(newDeps);
        if (earliestPostStart) {
          const newEndDate = getPreviousWorkingDay(earliestPostStart);
          const newStartDate = calculateStartDate(newEndDate, prev.duration);

          // Check if new start date conflicts with prior dependencies
          const latestPriorEnd = getLatestPriorEndDate(prev.prior_deps);
          const minStartDate = latestPriorEnd ? getNextWorkingDay(latestPriorEnd) : null;

          newFormData = {
            ...newFormData,
            planned_start: minStartDate && newStartDate < minStartDate ? minStartDate : newStartDate,
            planned_end: newEndDate,
          };
        }
      }

      return newFormData;
    });
  };

  // Filter out current task and tasks in other phases from dependency options
  const availableTasks = allTasks.filter(
    (t) => t.id !== task?.id && t.phase_id === phaseId
  );

  // Tasks from other phases for cross-phase dependencies
  const otherPhaseTasks = allTasks.filter(
    (t) => t.id !== task?.id && t.phase_id !== phaseId
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isEditing ? t('tasks.editTask', 'Edit Task') : t('tasks.add')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {phaseName}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-gray-500 dark:text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Task Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('tasks.form.name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.task_name}
                    onChange={(e) =>
                      setFormData({ ...formData, task_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t('tasks.form.namePlaceholder')}
                  />
                </div>

                {/* Task Owner */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('tasks.form.owner')} *
                  </label>
                  <EmployeeSelect
                    value={formData.task_owner}
                    onChange={(value) =>
                      setFormData({ ...formData, task_owner: value })
                    }
                    placeholder={t('tasks.form.ownerPlaceholder')}
                    required
                  />
                </div>

                {/* Status and Priority Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('tasks.columns.status')}
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as TaskStatus,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {TASK_STATUS_LABELS[status]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('tasks.form.priority')}
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priority: e.target.value as TaskPriority,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {PRIORITY_OPTIONS.map((priority) => (
                        <option key={priority} value={priority}>
                          {TASK_PRIORITY_LABELS[priority]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Duration and Dates Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('tasks.duration', 'Duration (hrs)')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.duration}
                      onChange={(e) => handleDurationChange(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('tasks.form.startDate')}
                      {dateConstraints.minStartDate && (
                        <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                          (≥ {dateConstraints.minStartDate})
                        </span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={formData.planned_start}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      min={dateConstraints.minStartDate}
                      max={dateConstraints.maxEndDate}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('tasks.form.endDate')}
                      {dateConstraints.maxEndDate ? (
                        <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                          (≤ {dateConstraints.maxEndDate})
                        </span>
                      ) : (
                        <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                          ({t('tasks.autoCalculated', 'auto')})
                        </span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={formData.planned_end}
                      onChange={(e) =>
                        setFormData({ ...formData, planned_end: e.target.value })
                      }
                      min={dateConstraints.minStartDate || formData.planned_start}
                      max={dateConstraints.maxEndDate}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Prior Dependencies */}
                {(availableTasks.length > 0 || otherPhaseTasks.length > 0) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('tasks.priorDeps', 'Prior Dependencies')}
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                        ({t('tasks.priorDepsHint', 'must complete before this task')})
                      </span>
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-1 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      {availableTasks.length > 0 && (
                        <>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {t('tasks.samePhase', 'Same Phase')}
                          </p>
                          {availableTasks.map((t) => (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={formData.prior_deps.includes(t.id)}
                                onChange={() => handleDependencyToggle('prior_deps', t.id)}
                                className="rounded border-gray-300 dark:border-gray-600 text-emerald-500 focus:ring-emerald-500"
                              />
                              <span className="text-gray-700 dark:text-gray-300">
                                {t.task_name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({TASK_STATUS_LABELS[t.status]})
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                      {otherPhaseTasks.length > 0 && (
                        <>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 mb-1">
                            {t('tasks.otherPhases', 'Other Phases')}
                          </p>
                          {otherPhaseTasks.map((t) => (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={formData.prior_deps.includes(t.id)}
                                onChange={() => handleDependencyToggle('prior_deps', t.id)}
                                className="rounded border-gray-300 dark:border-gray-600 text-emerald-500 focus:ring-emerald-500"
                              />
                              <span className="text-gray-700 dark:text-gray-300">
                                {t.task_name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({t.phase_name} - {TASK_STATUS_LABELS[t.status]})
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Post Dependencies */}
                {(availableTasks.length > 0 || otherPhaseTasks.length > 0) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('tasks.postDeps', 'Post Dependencies')}
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                        ({t('tasks.postDepsHint', 'depend on this task')})
                      </span>
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-1 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      {availableTasks.length > 0 && (
                        <>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {t('tasks.samePhase', 'Same Phase')}
                          </p>
                          {availableTasks.map((t) => (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={formData.post_deps.includes(t.id)}
                                onChange={() => handleDependencyToggle('post_deps', t.id)}
                                className="rounded border-gray-300 dark:border-gray-600 text-emerald-500 focus:ring-emerald-500"
                              />
                              <span className="text-gray-700 dark:text-gray-300">
                                {t.task_name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({TASK_STATUS_LABELS[t.status]})
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                      {otherPhaseTasks.length > 0 && (
                        <>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 mb-1">
                            {t('tasks.otherPhases', 'Other Phases')}
                          </p>
                          {otherPhaseTasks.map((t) => (
                            <label
                              key={t.id}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={formData.post_deps.includes(t.id)}
                                onChange={() => handleDependencyToggle('post_deps', t.id)}
                                className="rounded border-gray-300 dark:border-gray-600 text-emerald-500 focus:ring-emerald-500"
                              />
                              <span className="text-gray-700 dark:text-gray-300">
                                {t.task_name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({t.phase_name} - {TASK_STATUS_LABELS[t.status]})
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Review Note */}
                {isReviewStatus && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      {t('tasks.reviewNote', 'Task is in Review status. Attachments can be managed from the teamwork view.')}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleteTask.isLoading}
                      className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteTask.isLoading ? t('common.deleting', 'Deleting...') : t('delete')}
                    </button>
                  ) : (
                    <div />
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={createTask.isLoading || updateTask.isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {createTask.isLoading || updateTask.isLoading
                        ? t('common.saving')
                        : isEditing
                        ? t('common.save')
                        : t('common.create')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectTaskModal;
